import * as Sentry from '@sentry/browser';
import React from 'react';

interface Props {
  children: React.ReactNode;
}
interface State {
  error?: Error;
}

/**
 * Outermost boundary, above every provider and above the router.
 *
 * React unmounts the whole tree when a render throws and nothing catches it,
 * which presents as a blank white page. That is indistinguishable from the app
 * being dead, and on a phone there is no console to check. This turns any
 * startup crash into something readable on screen.
 *
 * Deliberately standalone rather than reusing components/common/errorBoundary:
 * that one reads callSnackbar off the global Context, which is exactly the
 * machinery that may have failed to initialise by the time we get here.
 */
export default class StartupBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: any) {
    // console.error survives disableLoggingInProduction; debug/info/log do not.
    console.error('Startup failure', error, info);

    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', info?.componentStack);
      Sentry.captureException(error);
    });
  }

  render() {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          font: '12px/1.5 ui-monospace, Menlo, Consolas, monospace',
          margin: 0,
          padding: 16,
          color: '#111',
          background: '#ffe9e9',
          borderBottom: '3px solid #b00',
        }}
      >
        {`Fadila failed to start\n\n${error.message}\n\n${error.stack || ''}`}
      </pre>
    );
  }
}
